import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { fetchInbox } from '@/services/communicationApi'

export interface InboxCounts {
  unreadNotifications: number
  unreadCircleNotifications: number
  // Unread event notifications — own the Events tab badge, excluded from the
  // Messages badge below (same partition as circle notifications).
  unreadEventNotifications: number
  // Pending circle invitations awaiting accept/decline. Drives the Circles tab
  // badge independently of notification read-state, so reading the invite's
  // notification in Messages doesn't clear the badge while the invite is live.
  pendingCircleInvitations: number
  unreadMessages: number
  pendingAcknowledgments: number
}

// Messages-tab badge: unread messages + notifications that aren't owned by
// another tab (circle + event notifications have their own badges, so they're
// excluded here to avoid double-counting). Both are subsets of
// unreadNotifications; clamp at 0 defensively.
export function unreadMessagesBadgeCount(counts: InboxCounts): number {
  return (
    counts.unreadMessages +
    Math.max(
      0,
      counts.unreadNotifications - counts.unreadCircleNotifications - counts.unreadEventNotifications,
    )
  )
}

// Per-key unread counts for the mobile bottom tab bar. Each unread item is
// attributed to exactly one tab (no overlap between Messages, Circles, Events).
// Circles = unread circle notifications + pending circle invitations; the
// backend excludes invitation notifications from unreadCircleNotifications so
// the two terms never double-count the same invite.
export function bottomTabBadgesFor(counts: InboxCounts): Record<string, number> {
  return {
    associations: counts.pendingAcknowledgments,
    events: counts.unreadEventNotifications,
    circles: counts.unreadCircleNotifications + counts.pendingCircleInvitations,
    messages: unreadMessagesBadgeCount(counts),
  }
}

const POLL_INTERVAL = 60_000 // refresh every 60s

export function useInboxCounts() {
  const { token } = useAuth()
  const [counts, setCounts] = useState<InboxCounts>({
    unreadNotifications: 0,
    unreadCircleNotifications: 0,
    unreadEventNotifications: 0,
    pendingCircleInvitations: 0,
    unreadMessages: 0,
    pendingAcknowledgments: 0,
  })

  const refresh = useCallback(async () => {
    if (!token) return
    try {
      const data = await fetchInbox(token)
      setCounts({
        unreadNotifications: data.counts?.unreadNotifications ?? 0,
        unreadCircleNotifications: data.counts?.unreadCircleNotifications ?? 0,
        unreadEventNotifications: data.counts?.unreadEventNotifications ?? 0,
        pendingCircleInvitations: data.counts?.pendingCircleInvitations ?? 0,
        unreadMessages: data.counts?.unreadMessages ?? 0,
        pendingAcknowledgments: data.counts?.pendingAcknowledgments ?? 0,
      })
    } catch {
      // Silently fail — badge counts are non-critical
    }
  }, [token])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, POLL_INTERVAL)
    return () => clearInterval(id)
  }, [refresh])

  return { counts, refresh }
}
